import { createHtmlAnchorPattern, createMarkdownLinkPattern } from "src/app/utils/extract-sources.util";

type SourceRecord = any;

type CitationDecision = { action: "remove" } | { action: "renumber"; newNumber: number } | { action: "keep" };

export interface SourceCitationKeys {
  urlKey: string;
  checkedKey: string;
  renumberCheckedSources: boolean;
}

export const EXCLUDED_SOURCE_NUMBER = "-";

export type SourceNumber = number | typeof EXCLUDED_SOURCE_NUMBER;

/** Unchecked sources lose their number, so the visible numbering stays contiguous over the checked ones. */
export function getSourceNumber(sources: SourceRecord[], index: number, checkedKey: string, renumberCheckedSources: boolean): SourceNumber {
  if (!renumberCheckedSources) {
    return index + 1;
  }

  if (!sources[index]?.[checkedKey]) {
    return EXCLUDED_SOURCE_NUMBER;
  }

  let count = 0;
  for (let i = 0; i <= index; i++) {
    if (sources[i]?.[checkedKey]) {
      count += 1;
    }
  }
  return count;
}

/** Unchecked last, original order preserved within each group. */
export function sortedSourceIndices(sources: SourceRecord[], checkedKey: string): number[] {
  return sources
    .map((_, index) => index)
    .sort((a, b) => {
      const aChecked = !!sources[a]?.[checkedKey];
      const bChecked = !!sources[b]?.[checkedKey];
      if (aChecked === bChecked) {
        return a - b;
      }
      return aChecked ? 1 : -1;
    });
}

/** Matched by URL, so a marker someone wrote by hand is left alone. */
export function renderCitations(markdown: string | null, sources: SourceRecord[], keys: SourceCitationKeys): string | null {
  if (!markdown || !sources.length) {
    return markdown;
  }

  const sourceIndexByUrl = buildSourceIndexByUrl(sources, keys.urlKey);

  return markdown
    .replace(createMarkdownLinkPattern(), (fullMatch: string, label: string, url: string) =>
      applyCitation(decideCitation(sources, sourceIndexByUrl, url, label, keys), fullMatch, newNumber => `[${newNumber}](${url})`)
    )
    .replace(createHtmlAnchorPattern(), (fullMatch: string, url: string, innerLabel: string) =>
      applyCitation(decideCitation(sources, sourceIndexByUrl, url, innerLabel, keys), fullMatch, newNumber => {
        const openTag = fullMatch.slice(0, fullMatch.indexOf(">") + 1);
        const cleanedLabel = innerLabel.replace(/<[^>]+>/g, "").trim();
        const newLabel = cleanedLabel.startsWith("[") ? `[${newNumber}]` : `${newNumber}`;
        return `${openTag}${newLabel}</a>`;
      })
    );
}

function buildSourceIndexByUrl(sources: SourceRecord[], urlKey: string): Map<string, number> {
  const sourceIndexByUrl = new Map<string, number>();
  sources.forEach((source, index) => {
    const url = (source?.[urlKey] || "").trim();
    if (url && !sourceIndexByUrl.has(url)) {
      sourceIndexByUrl.set(url, index);
    }
  });
  return sourceIndexByUrl;
}

function applyCitation(decision: CitationDecision, fullMatch: string, formatRenumbered: (newNumber: number) => string): string {
  if (decision.action === "remove") return "";
  if (decision.action === "renumber") return formatRenumbered(decision.newNumber);
  return fullMatch;
}

function decideCitation(
  sources: SourceRecord[],
  sourceIndexByUrl: Map<string, number>,
  url: string,
  rawLabel: string,
  keys: SourceCitationKeys
): CitationDecision {
  const sourceIndex = sourceIndexByUrl.get((url || "").trim());
  if (sourceIndex === undefined) {
    return { action: "keep" };
  }

  if (!sources[sourceIndex]?.[keys.checkedKey]) {
    return { action: "remove" };
  }

  const cleanedLabel = (rawLabel || "").replace(/<[^>]+>/g, "").trim();
  if (!/^\[?\d+\]?$/.test(cleanedLabel)) {
    return { action: "keep" };
  }

  return { action: "renumber", newNumber: getSourceNumber(sources, sourceIndex, keys.checkedKey, keys.renumberCheckedSources) as number };
}
